/**
 * src/components/shared/AiDraftBlock.jsx — unified AI email-draft assist (CEO 2026-07-03).
 *
 * ONE reusable module: a persistent "AI notes (bullets)" field + reply-language
 * select + mic dictation + Draft-with-AI button. Expands the notes into the caller's
 * message body (server-side RAG-grounded via leads.draft_reply) WITHOUT erasing the
 * notes — you see your prompt, tweak it, and re-draft to iterate. Every email composer
 * (ComposeEmailModal, EmailEntry, …) mounts THIS, so the draft UX improves in one place
 * instead of drifting across parallel copies.
 *
 * Props:
 *   dealId   — string (required); the deal whose context grounds the draft
 *   hasBody  — bool; the caller already holds draft/body text (→ button reads "Redraft")
 *   onDraft  — (draft:{ subject, body, language }) => void; caller writes it to its
 *              own subject/body state. AiDraftBlock never owns the outgoing message,
 *              only the prompt (notes) — so drafting can't erase what the caller shows.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { Sparkles, Loader, Mic, Square, AlertTriangle } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';
import { useToast } from './ToastProvider';
import { draftLeadReply, getStoredReplyDraft } from '../../lib/api/leads.js';
import useDictation from '../../hooks/useDictation.js';
import DictationRecoveryBar from './DictationRecoveryBar.jsx';

/** Reply-language → Whisper ISO-639-1 (STT biases better with a hint; '' → auto-detect). */
const STT_LANG = { English: 'en', Spanish: 'es', Catalan: 'ca', French: 'fr' };

/**
 * One-tap corrections (TICKET-972, CEO decision Q4: tone + action sets; learned chips
 * later from the reply audit ledger). The instruction text goes to the model in English;
 * the reply keeps its own language.
 */
export const REPLY_CHIPS = Object.freeze([
  { key: 'shorter', instruction: 'Make it shorter.' },
  { key: 'warmer', instruction: 'Make it warmer and friendlier.' },
  { key: 'technical', instruction: 'Make it more technical.' },
  { key: 'formal', instruction: 'Make it more formal.' },
  { key: 'photos', instruction: 'Ask for the photos we still need (a specific view, not photos they already sent).' },
  { key: 'call', instruction: 'Suggest a short call as the next step.' },
  { key: 'visit', instruction: 'Suggest a visit to the boat as the next step.' },
  { key: 'noPrice', instruction: 'Do not mention any price.' },
]);

/**
 * Extra props (TICKET-971/972, EmailEntry only — the compose modal keeps the plain block):
 *   leadEmailId   the inbound being answered -> grounded generator on the server
 *   currentBody / currentSubject  the text in the composer -> chips and instructions REFINE it
 *   autoDraft     draft once on open when the composer is empty
 *   showChips     render the one-tap corrections + the scope/missing/next line
 */
export default function AiDraftBlock({
  dealId, leadEmailId = null, hasBody = false, onDraft,
  currentBody = '', currentSubject = '', autoDraft = false, showChips = false,
}) {
  const { t } = useT();
  const { showError } = useToast();
  const [notes, setNotes] = useState('');
  const [replyLang, setReplyLang] = useState(''); // '' = auto (mirror inbound); else English/Spanish/Catalan/French
  const [drafting, setDrafting] = useState(false);
  const [result, setResult] = useState(null); // { status, meta, review } from the grounded generator
  const notesFromVoice = useRef(false); // audit ledger: the instruction was dictated vs typed

  // Mic dictates into the NOTES (the prompt), never the outgoing body.
  // TICKET-324: surface the hook's REAL message. Collapsing every failure
  // into t('composeEmail.micError') is how a 5m11s note reported itself as
  // "Couldn't capture audio — try again" when it was actually too large,
  // saved, and recoverable. The generic string stays as the fallback only.
  const dictation = useDictation({
    onTranscript: (text) => { notesFromVoice.current = true; setNotes((n) => (n ? `${n}\n${text}` : text)); },
    onError: (msg) => showError(msg || t('composeEmail.micError')),
    language: STT_LANG[replyLang] || 'auto',
  });
  const { recording, transcribing, audioLevel, toggle: toggleMic } = dictation;

  // instruction: a chip's text (one tap) — combined with any typed/dictated notes. With a
  // body already in the composer, the server REFINES that text instead of drafting anew.
  // Latest composer text, readable when an async draft lands (an auto draft must never
  // replace text the user typed while it was generating).
  const bodyRef = useRef(currentBody);
  bodyRef.current = currentBody;

  const handleDraft = useCallback(async (instruction = '', { auto = false } = {}) => {
    if (!dealId || drafting) return;
    setDrafting(true);
    try {
      const refine = !!(leadEmailId && currentBody && currentBody.trim());
      const res = await draftLeadReply(dealId, {
        notes: [instruction, notes].filter(Boolean).join('\n'),
        language: replyLang,
        ...(leadEmailId ? { leadEmailId } : {}),
        ...(refine ? { previousBody: currentBody, previousSubject: currentSubject } : {}),
        ...(instruction ? { instructionKind: 'chip' } : notes ? { instructionKind: notesFromVoice.current ? 'voice' : 'typed' } : {}),
      });
      if (res?.ok && res.draft) {
        if (auto && bodyRef.current && bodyRef.current.trim()) return; // user started writing — keep their text
        onDraft?.(res.draft_id ? { ...res.draft, draftId: res.draft_id } : res.draft);
        setResult(res.status ? { status: res.status, meta: res.meta || null, review: res.review || [] } : null);
      } else showError(t('composeEmail.error'));
    } catch (err) {
      console.warn('[AiDraftBlock] draft failed:', err?.message);
      showError(t('composeEmail.error'));
    } finally {
      setDrafting(false);
    }
  }, [dealId, leadEmailId, drafting, notes, replyLang, currentBody, currentSubject, onDraft, showError, t]);

  // Draft on open (CEO: "when an email is opened, a reply should already be suggested").
  // Once per mount, only into an empty composer — never over a saved or typed draft.
  // TICKET-975: first use the draft pre-generated on ingest (no wait, no spend); only if
  // there is none — or it went stale — generate one now.
  const autoFired = useRef(false);
  useEffect(() => {
    if (!autoDraft || autoFired.current || hasBody || !leadEmailId) return;
    autoFired.current = true;
    (async () => {
      try {
        const stored = await getStoredReplyDraft(dealId, leadEmailId);
        if (stored?.ok && stored.draft) {
          if (bodyRef.current && bodyRef.current.trim()) return; // user started writing
          onDraft?.(stored.draft_id ? { ...stored.draft, draftId: stored.draft_id } : stored.draft);
          setResult({ status: stored.status, meta: stored.meta || null, review: stored.review || [] });
          return;
        }
      } catch { /* no stored draft available — fall through to a fresh one */ }
      handleDraft('', { auto: true });
    })();
  }, [autoDraft, hasBody, leadEmailId, dealId, onDraft, handleDraft]);

  const fieldStyle = {
    width: '100%', minHeight: 44, borderRadius: 8, fontSize: 13,
    padding: '10px 12px', outline: 'none', boxSizing: 'border-box',
    background: 'var(--card)', color: 'var(--t1)',
    boxShadow: 'inset 0 0 0 1px var(--bdr)',
  };

  return (
    <div data-testid="ai-draft-block">
      <label
        htmlFor="ai-draft-notes"
        style={{ fontSize: 12, fontWeight: 600, color: 'var(--t2)', marginBottom: 4, display: 'block' }}
      >
        {t('composeEmail.aiNotes')}
      </label>
      <textarea
        id="ai-draft-notes" data-testid="ai-draft-notes"
        value={notes} onChange={(e) => { notesFromVoice.current = false; setNotes(e.target.value); }}
        placeholder={t('composeEmail.aiNotesPlaceholder')} rows={3}
        style={{ ...fieldStyle, minHeight: 72, resize: 'vertical', lineHeight: 1.5 }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {/* Reply language — '' = auto (mirror inbound); the drafter opens with a warm
            note when you reply in a different language (e.g. Catalan → Spanish). */}
        <select
          data-testid="ai-draft-lang"
          value={replyLang}
          onChange={(e) => setReplyLang(e.target.value)}
          aria-label={t('composeEmail.replyLanguage')}
          style={{ ...fieldStyle, width: 'auto', minHeight: 44, padding: '8px 12px', cursor: 'pointer' }}
        >
          <option value="">{t('composeEmail.langAuto')}</option>
          <option value="English">English</option>
          <option value="Spanish">Español</option>
          <option value="Catalan">Català</option>
          <option value="French">Français</option>
        </select>
        <button
          type="button"
          data-testid="ai-draft-mic"
          onClick={toggleMic}
          disabled={transcribing}
          aria-pressed={recording}
          aria-label={recording ? t('composeEmail.micListening') : t('composeEmail.mic')}
          title={recording ? t('composeEmail.micListening') : t('composeEmail.mic')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            minHeight: 44, minWidth: 44, padding: '8px 14px', borderRadius: 10, border: 'none',
            background: recording ? 'var(--accent)' : 'var(--glass)',
            color: recording ? 'var(--card)' : 'var(--t2)',
            fontSize: 13, fontWeight: 600, cursor: transcribing ? 'default' : 'pointer',
            opacity: transcribing ? 0.6 : 1,
            boxShadow: recording ? 'none' : 'inset 0 0 0 1px var(--bdr)',
          }}
        >
          {transcribing
            ? <Loader size={16} strokeWidth={1.75} style={{ animation: 'spin 800ms linear infinite' }} />
            : recording
              ? <Square size={16} strokeWidth={1.75} />
              : <Mic size={16} strokeWidth={1.75} />}
          {recording && (
            <span style={{
              width: 8, height: 8, borderRadius: 999, background: 'var(--card)',
              opacity: 0.35 + Math.min(1, audioLevel) * 0.65,
            }} />
          )}
        </button>
        <button
          type="button"
          data-testid="ai-draft-btn"
          onClick={() => handleDraft()}
          disabled={drafting}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            minHeight: 44, padding: '8px 14px', borderRadius: 10, border: 'none',
            background: 'var(--accent-bg)', color: 'var(--accent)',
            fontSize: 13, fontWeight: 600, cursor: drafting ? 'default' : 'pointer',
            opacity: drafting ? 0.6 : 1, boxShadow: 'inset 0 0 0 1px var(--bdr)',
          }}
        >
          {drafting
            ? <Loader size={16} strokeWidth={1.75} style={{ animation: 'spin 800ms linear infinite' }} />
            : <Sparkles size={16} strokeWidth={1.75} />}
          {drafting ? t('composeEmail.aiDrafting') : (hasBody ? t('composeEmail.aiRedraft') : t('composeEmail.aiDraft'))}
        </button>
      </div>
      <DictationRecoveryBar dictation={dictation} />
      {showChips && (
        <div data-testid="ai-draft-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {REPLY_CHIPS.map((c) => (
            <button
              key={c.key}
              type="button"
              data-testid={`ai-draft-chip-${c.key}`}
              onClick={() => handleDraft(c.instruction)}
              disabled={drafting}
              style={{
                minHeight: 44, padding: '6px 12px', borderRadius: 999, border: 'none',
                background: 'var(--glass)', color: 'var(--t2)', fontSize: 12, fontWeight: 600,
                cursor: drafting ? 'default' : 'pointer', opacity: drafting ? 0.6 : 1,
                boxShadow: 'inset 0 0 0 1px var(--bdr)',
              }}
            >
              {t(`composeEmail.chips.${c.key}`)}
            </button>
          ))}
        </div>
      )}
      {showChips && result?.meta && (
        <p data-testid="ai-draft-meta" style={{ margin: '8px 2px 0', fontSize: 12, color: 'var(--t2)', lineHeight: 1.4 }}>
          {[
            result.meta.scope_status && `${t('composeEmail.scope')}: ${t(`composeEmail.scopeStatus.${result.meta.scope_status}`)}`,
            result.meta.missing?.length ? `${t('composeEmail.missing')}: ${result.meta.missing.join(', ')}` : null,
          ].filter(Boolean).join(' · ')}
        </p>
      )}
      {showChips && result?.status === 'needs_review' && (
        <div
          data-testid="ai-draft-review"
          role="status"
          style={{
            display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, padding: '8px 10px', borderRadius: 8,
            background: 'var(--warn-tint)', color: 'var(--t1)', fontSize: 12, lineHeight: 1.4,
          }}
        >
          <AlertTriangle size={16} strokeWidth={1.5} style={{ flexShrink: 0, color: 'var(--warn)' }} />
          <div>
            <strong>{t('composeEmail.needsReview')}</strong>
            {result.review.filter((r) => r.sentence).map((r) => (
              <div key={`${r.code}-${r.sentence}`}>“{r.sentence}”</div>
            ))}
          </div>
        </div>
      )}
      <p style={{ margin: '6px 2px 0', fontSize: 11, color: 'var(--tm)', lineHeight: 1.4 }}>
        {t('composeEmail.aiNotesHint')}
      </p>
    </div>
  );
}
